import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Stars } from '../components/Shared';
import './Book.css';

const TIME_SLOTS = ['09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM'];

export default function BookDoctor() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    bookingDate: '',
    timeSlot: '',
    notes: ''
  });

  useEffect(() => {
    if (!authUser || authUser.role !== 'patient') {
      navigate('/login?role=patient');
      return;
    }
    axios.get(`/api/doctors/${doctorId}`)
      .then(r => { setDoctor(r.data.doctor); setLoading(false); })
      .catch(() => setLoading(false));
  }, [doctorId, authUser, navigate]);

  const h = e => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bookingDate || !form.timeSlot)
      return setError('Please fill all required fields');
    setError('');
    setSubmitting(true);
    try {
      await axios.post('/api/appointments', {
        doctorId: doctor.user._id,
        doctorProfileId: doctor._id,
        bookingDate: form.bookingDate,
        timeSlot: form.timeSlot,
        notes: form.notes
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Booking failed');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="page-loader">Loading…</div>;
  if (!doctor) return <div className="page-loader">Doctor not found</div>;

  if (success) return (
    <div className="book-success">
      <div className="success-icon">🎉</div>
      <h2>Consultation Booked!</h2>
      <p>Your appointment with <strong>Dr. {doctor.user?.firstName} {doctor.user?.lastName}</strong> has been confirmed.</p>
      <p>You will receive a video call link shortly before the appointment.</p>
      <div className="success-actions">
        <button className="btn btn-teal" onClick={() => navigate('/patient/appointments')}>View My Appointments</button>
        <button className="btn btn-outline" onClick={() => navigate('/doctors')}>Find More Doctors</button>
      </div>
    </div>
  );

  return (
    <div className="book-page">
      <div className="container book-layout">
        {/* Doctor summary */}
        <div className="book-sidebar">
          <div className="card">
            <h3>You're booking</h3>
            <div className="book-nurse-info">
              <div className="bni-avatar">{doctor.user?.firstName?.[0]}{doctor.user?.lastName?.[0]}</div>
              <div>
                <strong>Dr. {doctor.user?.firstName} {doctor.user?.lastName}</strong>
                <span>{doctor.specialization}</span>
                <Stars rating={doctor.rating} size={13}/>
              </div>
            </div>
            <div className="book-rate-info">
              <div><span>Consultation Fee</span><strong>₨{doctor.consultationFee}</strong></div>
              <div><span>Duration</span><strong>30 minutes</strong></div>
              <div className="book-total"><span>Total</span><strong>₨{doctor.consultationFee}</strong></div>
            </div>
            {doctor.isVerified && <div className="badge badge-teal" style={{width:'100%',justifyContent:'center',marginTop:'12px'}}>✅ Verified Doctor</div>}
          </div>
        </div>

        {/* Booking form */}
        <div className="book-form-wrap">
          <h1>Book Consultation</h1>
          <p className="book-sub">Schedule an online consultation with the doctor</p>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={submit}>
            <div className="card form-section">
              <h3>Consultation Details</h3>
              <div className="form-row">
                <div className="field">
                  <label>Date *</label>
                  <input
                    type="date"
                    name="bookingDate"
                    value={form.bookingDate}
                    onChange={h}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>
              <div className="field">
                <label>Time Slot (30 minutes) *</label>
                <div className="time-slots">
                  {TIME_SLOTS.map(t => (
                    <button
                      type="button"
                      key={t}
                      className={`time-slot ${form.timeSlot === t ? 'selected' : ''}`}
                      onClick={() => setForm({...form, timeSlot: t})}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="card form-section">
              <h3>Additional Information</h3>
              <div className="field">
                <label>Reason for Consultation</label>
                <textarea
                  name="notes"
                  placeholder="Describe your symptoms or reason for consultation…"
                  value={form.notes}
                  onChange={h}
                  rows="4"
                />
              </div>
            </div>

            <div className="book-submit-wrap">
              <div className="book-price">
                <span>Consultation Fee: </span>
                <strong>₨{doctor.consultationFee}</strong>
              </div>
              <button
                type="submit"
                className="btn btn-teal"
                disabled={submitting}
                style={{padding:'14px 36px'}}
              >
                {submitting ? <span className="spinner"/> : '📅 Confirm Appointment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

